import RefluencedLogo from "@/assets/images/refluenced.svg?react";
import Button from "@/components/Common/Button";
import DownloadAppButton from "@/components/Common/DownloadAppButton";
import InputField from "@/components/Common/forms/InputField";
import PasswordField from "@/components/Common/forms/PasswordField";
import TextButton from "@/components/Common/TextButton";
import AuthPageHeader from "@/components/registration/AuthPageHeader";
import GoogleLoginButton from "@/components/registration/GoogleLoginButton";
import {useLogin} from "@/hooks/useLogin";
import {useZodState} from "@/hooks/useZodState";
import LoginPageTabMenu from "@/pages/login/LoginPageTabMenu";
import {locationAtom} from "@/store/jotaiAtoms";
import useRegistrationStore from "@/store/registration";
import {createFileRoute} from "@tanstack/react-router";
import {getDefaultStore} from "jotai";
import {useTranslation} from "react-i18next";
import {useKeyPressEvent, useMedia, useTitle} from "react-use";
import {z} from "zod";

const loginSchema = z.object({
  email: z.email("invalidEmail"),
  password: z.string().min(1, "passwordIsRequired"),
});

export const Route = createFileRoute("/_unauthenticated/login")({
  validateSearch: (search: Record<string, unknown>): {redirect?: string} => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  beforeLoad: ({search}) => {
    // Bridge the post-login destination (captured on session expiry / logout as
    // a URL param so it survives a full page reload) into locationAtom, which
    // the "/" route reads after login. Guard against open redirects.
    if (search.redirect?.startsWith("/") && !search.redirect.startsWith("//")) {
      getDefaultStore().set(locationAtom, {previousRoute: search.redirect, currentRoute: search.redirect});
    }
  },
  component: Login,
});

function Login() {
  const {t} = useTranslation("common");
  const navigate = Route.useNavigate();
  const isSmallScreen = useMedia("(max-width: 640px)");
  const isTall = useMedia("(min-height: 1200px)");
  const email = useRegistrationStore(state => state.email);
  const password = useRegistrationStore(state => state.password);
  const setValue = useRegistrationStore(state => state.setValue);
  const [form, setFormByKey, validate, setState] = useZodState(loginSchema, {email, password});
  const {login, isPending} = useLogin(e => {
    setState(d => {
      d.errors.overall = (e.response?.data.detail as string | undefined) ?? "somethingWentWrong";
    });
  });

  useTitle(t("login"));
  useKeyPressEvent("Enter", () => validate() && login(form));

  return (
    <div className="w-full px-4 sm:px-8 md:px-12">
      <div
        className={
          "flex h-full w-full flex-col items-center sm:px-20 lg:px-8 xl:px-20 " +
          (isTall ? "justify-center gap-10 pb-28" : "gap-4 pb-10 sm:gap-8")
        }>
        <LoginPageTabMenu />
        <AuthPageHeader title={t("login_brand_account_title")} />
        <div className="size-20 shrink-0">
          <RefluencedLogo className="size-full object-contain" />
        </div>
        {!isSmallScreen && <div className="h-[5vh]" />}
        <div className="flex w-full flex-col gap-5">
          <InputField
            customFieldId="login-email-field"
            dataTestId="login-email-field"
            onChange={newValue => {
              setValue("email", newValue);
              setFormByKey("email", newValue);
              setValue("isEmailValidated", false);
            }}
            value={form.email}
            placeholder={t("email")}
            label={t("email")}
            error={form.errors.email ? t(form.errors.email as "invalidEmail") : undefined}
            type="email"
          />
          <PasswordField
            customFieldId="login-password-field"
            dataTestId="login-password-field"
            onChange={newValue => {
              setValue("password", newValue);
              setFormByKey("password", newValue);
            }}
            value={form.password}
            error={form.errors.password ? t(form.errors.password as "passwordIsRequired") : null}
            placeholder={t("Password")}
            label={t("Password")}
          />
          {!!form.errors.overall && (
            <span data-testid="login-error-message" className="text-danger-medium-red mt-0.5 text-xs">
              {t(form.errors.overall as "somethingWentWrong")}
            </span>
          )}
        </div>

        <div className="flex flex-col items-center gap-5 font-medium">
          <Button
            customButtonId="login-button"
            dataTestId="login-button"
            onClick={() => validate() && login(form)}
            isLoading={isPending}
            className="h-15 w-full sm:w-105"
            buttonStyle={"Gradient"}>
            {t("log_in")}
          </Button>
          <div className="flex w-full max-w-105 items-center gap-5">
            <div className="bg-primary-light-solid h-px flex-1" />
            <span className="text-neutral-grey-dark text-xs">{t("or")}</span>
            <div className="bg-primary-light-solid h-px flex-1" />
          </div>
          <GoogleLoginButton className="h-15 w-full sm:w-105" />
          <TextButton onClick={() => navigate({to: "/request-reset"})} text={t("forgotYourPassword")} />
        </div>
        <DownloadAppButton />
      </div>
    </div>
  );
}
